import React, { useState } from 'react';
import { AppBar, Badge, Box, Drawer, IconButton, Menu, MenuItem, Toolbar, Tooltip, Typography } from '@mui/material';
import {
    AccountCircle,
    ChevronLeft,
    Fullscreen,
    Mail as MailIcon,
    Menu as MenuIcon,
    MoreVert as MoreIcon,
} from '@mui/icons-material';
import Navigation from './Navigation';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'store';
import AppSearch from './AppSearch';
import layoutSx from './Layout.styles';
import { useTheme } from '@mui/material/styles';
import ClinicSelection from 'pages/ClinicSelection';

const Layout = ({ children }) => {
    const [state, setState] = useState({
        anchorEl: null,
        mobileMoreAnchorEl: null,
    });

    const dispatch = useDispatch(),
        toggleMenu = () => dispatch(actions.layout.toggleMenu());

    const theme = useTheme();
    const sx = layoutSx(theme);

    const menuOpen = useSelector((state) => state.layout.menuOpenTemporary || state.layout.menuOpen),
        title = useSelector((state) => state.layout.title),
        useLayout = useSelector((state) => state.layout.useLayout),
        showLogin = useSelector((state) => state.auth.showLoginPage),
        showClinicSelection = useSelector((state) => state.auth.showClinicSelection);

    const handleProfileMenuOpen = (event) => {
        setState({ ...state, anchorEl: event.currentTarget });
    };

    const handleMenuClose = () => {
        setState({ ...state, anchorEl: null });
        handleMobileMenuClose();
    };

    const handleMobileMenuOpen = (event) => {
        setState({ ...state, mobileMoreAnchorEl: event.currentTarget });
    };

    const handleMobileMenuClose = () => {
        setState({ ...state, mobileMoreAnchorEl: null });
    };

    const toggleFullscreen = (event) => {
        let element = document.body;
        if (event instanceof HTMLElement) {
            element = event;
        }

        let isFullscreen = document.webkitIsFullScreen || document.mozFullScreen || false;
        element.requestFullScreen =
            element.requestFullScreen ||
            element.webkitRequestFullScreen ||
            element.mozRequestFullScreen ||
            function () {
                return false;
            };
        document.cancelFullScreen =
            document.cancelFullScreen ||
            document.webkitCancelFullScreen ||
            document.mozCancelFullScreen ||
            function () {
                return false;
            };

        isFullscreen ? document.cancelFullScreen() : element.requestFullScreen();
    };

    const renderMenu = () => {
        if (!useLayout) return null;

        return (
            <Menu
                anchorEl={state.anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(state.anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
                <MenuItem onClick={handleMenuClose}>My account</MenuItem>
            </Menu>
        );
    };

    const renderMobileMenu = () => {
        const { mobileMoreAnchorEl } = state;
        const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

        if (!useLayout) return null;

        return (
            <Menu
                anchorEl={mobileMoreAnchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={isMobileMenuOpen}
                onClose={handleMobileMenuClose}
            >
                <MenuItem>
                    <IconButton color="inherit" size="large">
                        <Badge badgeContent={4} color="secondary">
                            <MailIcon />
                        </Badge>
                    </IconButton>
                    <p>Messages</p>
                </MenuItem>
                <MenuItem onClick={handleProfileMenuOpen}>
                    <IconButton color="inherit" size="large">
                        <AccountCircle />
                    </IconButton>
                    <p>Profile</p>
                </MenuItem>
            </Menu>
        );
    };

    const { anchorEl } = state;
    const isMenuOpen = Boolean(anchorEl);

    if (!useLayout || showLogin) {
        return <Box sx={sx.root}>{children}</Box>;
    }

    if (showClinicSelection) {
        return (
            <Box sx={sx.root}>
                <ClinicSelection />
            </Box>
        );
    }

    return (
        <Box sx={sx.root}>
            <AppBar
                position="fixed"
                color="default"
                elevation={2}
                sx={{
                    ...sx.appBar,
                    ...(menuOpen ? sx.appBarOpen : sx.appBarClosed),
                }}
            >
                <Toolbar>
                    <Tooltip title="Toggle Menu">
                        <IconButton
                            color="inherit"
                            aria-label="Open drawer"
                            onClick={toggleMenu}
                            sx={sx.menuButton}
                            size="large"
                        >
                            {menuOpen ? <ChevronLeft /> : <MenuIcon />}
                        </IconButton>
                    </Tooltip>

                    <Typography sx={sx.title} variant="h6" color="inherit" noWrap>
                        {title}
                    </Typography>

                    <Box sx={sx.grow} />
                    <AppSearch />
                    <div>
                        <Tooltip title="Toggle Fullscreen">
                            <IconButton color="inherit" onClick={toggleFullscreen} size="large">
                                <Fullscreen />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <Box sx={sx.sectionDesktop}>
                        <Tooltip title="Account Options">
                            <IconButton
                                aria-owns={isMenuOpen ? 'material-appbar' : undefined}
                                aria-haspopup="true"
                                onClick={handleProfileMenuOpen}
                                color="inherit"
                                size="large"
                            >
                                <AccountCircle />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={sx.sectionMobile}>
                        <IconButton aria-haspopup="true" onClick={handleMobileMenuOpen} color="inherit" size="large">
                            <MoreIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {renderMenu()}
            {renderMobileMenu()}

            <Drawer
                variant="permanent"
                open={menuOpen}
                sx={{
                    ...sx.drawer,
                    ...(menuOpen ? sx.drawerOpen : sx.drawerClose),
                    '& .MuiDrawer-paper': {
                        ...(menuOpen ? sx.drawerOpen : sx.drawerClose),
                        width: menuOpen ? sx.drawerOpen.width : sx.drawerClose.width,
                    },
                }}
            >
                <Navigation />
            </Drawer>

            <Box component="main" sx={useLayout ? sx.content : {}}>
                <Box sx={useLayout ? sx.toolbar : {}} />
                {children}
            </Box>
        </Box>
    );
};

export default Layout;
